import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { ClipboardList, Loader2 } from 'lucide-react';
import type {
  WorkLogEdit,
  WorkLogResponse,
} from '../../services/api/types.gen';
import { formatDateTimeInLocalZone } from '../../utils/dateUtils';

interface EntityWorkLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fetches the log. Called each time the dialog opens, so the reader always
   * sees edits made since they last looked. Memoize it (`useCallback`) so a
   * parent re-render does not refetch.
   */
  load: () => Promise<WorkLogResponse>;
  title?: string;
  description?: string;
  /**
   * Display names for the record's columns. A field with no entry falls back
   * to its own name, humanized, so a new backend column still reads sensibly
   * without a frontend change.
   */
  fieldLabels?: Record<string, string>;
}

/** "kx_modifier" -> "Kx modifier"; fieldLabels wins when it names the field. */
const humanizeField = (
  field: string,
  fieldLabels?: Record<string, string>
): string => {
  const mapped = fieldLabels?.[field];
  if (mapped) return mapped;
  const spaced = field.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** "first last", falling back to the email, then to an explicit Unknown. */
const editorName = (edit: WorkLogEdit): string => {
  if (!edit.editing_user) return 'System';
  const { first_name, last_name, email } = edit.editing_user;
  const name = [first_name, last_name].filter(Boolean).join(' ').trim();
  return name || email || 'Unknown';
};

/**
 * Who changed what, and when, for one record.
 *
 * Generic on purpose: the NOA is the first caller (EMR-2011), and any other
 * record with a `WorkLogResponse` endpoint can reuse it by passing its own
 * `load`.
 */
export const EntityWorkLogDialog: React.FC<EntityWorkLogDialogProps> = ({
  open,
  onOpenChange,
  load,
  title = 'Work log',
  description = 'Edits to this record, oldest first.',
  fieldLabels,
}) => {
  const [edits, setEdits] = useState<WorkLogEdit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .then(res => {
        if (cancelled) return;
        setEdits(res.work_log ?? []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load work log:', err);
        setError('Unable to load the work log. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ClipboardList className='h-5 w-5 text-primary' />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto pr-1'>
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : !edits || edits.length === 0 ? (
            <div className='py-6 text-sm text-muted-foreground'>
              No edits recorded yet. The work log tracks changes made after it
              was introduced, so earlier edits do not appear here.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {edits.map((edit, index) => {
                  const fields = Object.keys(edit.what_was_edited ?? {});
                  return (
                    <TableRow key={`${edit.action}-${edit.edited_at}-${index}`}>
                      <TableCell>{editorName(edit)}</TableCell>
                      <TableCell>
                        {edit.label}
                        {fields.length > 0 ? (
                          <span className='block text-xs text-muted-foreground'>
                            {fields
                              .map(field => humanizeField(field, fieldLabels))
                              .join(', ')}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {formatDateTimeInLocalZone(edit.edited_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
